import { useState, useEffect, useCallback } from 'react';
import { queueService } from './services/queueService';
import { RefreshCw, Pause, Play, Trash2, RotateCcw, AlertCircle, CheckCircle2, Clock, Loader2, Zap, TrendingUp } from 'lucide-react';

const POLL_INTERVAL = 5000;

export default function WhatsAppDashboardFila() {
  const [stats, setStats] = useState(null);
  const [enviosAtivos, setEnviosAtivos] = useState([]);
  const [failedJobs, setFailedJobs] = useState({ jobs: [], total: 0, page: 1, pages: 1 });
  const [blockedSessions, setBlockedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [s, ea, fj, bs] = await Promise.all([
        queueService.getStats(),
        queueService.getEnviosAtivos(),
        queueService.getJobs('failed', 1, 20),
        queueService.getBlockedSessions(),
      ]);
      setStats(s);
      setEnviosAtivos(ea);
      setFailedJobs(fj);
      setBlockedSessions(bs?.sessions || []);
    } catch (err) {
      console.error('Erro ao buscar dados da fila:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleAction = async (action, label) => {
    setActionLoading(label);
    try {
      await action();
      await fetchAll();
    } catch (err) {
      console.error(`Erro: ${label}`, err);
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-ink-1 sm:ml-16">
      <div className="px-5 sm:px-8 pt-8 pb-12 max-w-7xl mx-auto">

        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-in">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-positive-400 animate-glow-pulse" />
              <span className="label-overline text-positive-400/80">Worker ativo</span>
            </div>
            <h1 className="display-2 text-gradient-brand">
              Fila de Envio
            </h1>
            <p className="text-ink-3 text-sm">WhatsApp mass-send queue — BullMQ + concurrency 1.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleAction(fetchAll, 'refresh')}
              className="flex items-center gap-2 px-3 py-2 bg-surface-2 hover:bg-surface-3 rounded-lg text-sm transition"
              disabled={!!actionLoading}
            >
              <RefreshCw className={`w-4 h-4 ${actionLoading === 'refresh' ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            {stats?.isPaused ? (
              <button
                onClick={() => handleAction(queueService.resume, 'resume')}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm transition"
                disabled={!!actionLoading}
              >
                <Play className="w-4 h-4" />
                Retomar
              </button>
            ) : (
              <button
                onClick={() => handleAction(queueService.pause, 'pause')}
                className="flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm transition"
                disabled={!!actionLoading}
              >
                <Pause className="w-4 h-4" />
                Pausar
              </button>
            )}
            <button
              onClick={() => handleAction(queueService.clean, 'clean')}
              className="flex items-center gap-2 px-3 py-2 bg-surface-2 hover:bg-surface-3 rounded-lg text-sm transition"
              disabled={!!actionLoading}
            >
              <Trash2 className="w-4 h-4" />
              Limpar
            </button>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <StatCard icon={Clock} label="Aguardando" value={stats?.waiting || 0} color="text-amber-400" bg="bg-amber-400/10" />
          <StatCard icon={Zap} label="Ativo" value={stats?.active || 0} color="text-emerald-400" bg="bg-emerald-400/10" />
          <StatCard icon={Clock} label="Agendado" value={stats?.delayed || 0} color="text-blue-400" bg="bg-blue-400/10" />
          <StatCard icon={CheckCircle2} label="Enviados" value={stats?.completed || 0} color="text-green-400" bg="bg-green-400/10" />
          <StatCard icon={AlertCircle} label="Falhou" value={stats?.failed || 0} color="text-red-400" bg="bg-red-400/10" />
          <StatCard icon={TrendingUp} label="Pendentes" value={stats?.total || 0} color="text-cyan-400" bg="bg-cyan-400/10" />
        </div>

        {/* Blocked Sessions Alert */}
        {blockedSessions.map(session => (
          <div key={session} className="mb-6 px-5 py-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-red-300 font-semibold">Sessão {session} bloqueada — Múltiplas falhas detectadas</p>
                  <p className="text-red-400/70 text-sm mt-0.5">O WhatsApp desta sessão desconectou. Verifique a conexão e clique em continuar.</p>
                </div>
              </div>
              <button
                onClick={() => handleAction(() => queueService.resume(session), `resume-${session}`)}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold transition whitespace-nowrap"
                disabled={!!actionLoading}
              >
                <Play className={`w-4 h-4 ${actionLoading === `resume-${session}` ? 'animate-spin' : ''}`} />
                Desbloquear e Continuar
              </button>
            </div>
          </div>
        ))}

        {/* Global Paused Alert — prominent "Continuar" button */}
        {stats?.isPaused && (
          <div className="mb-6 px-5 py-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-red-300 font-semibold">Fila global pausada</p>
                  <p className="text-red-400/70 text-sm mt-0.5">A fila inteira foi pausada manualmente.</p>
                </div>
              </div>
              <button
                onClick={() => handleAction(queueService.resume, 'resume')}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold transition whitespace-nowrap"
                disabled={!!actionLoading}
              >
                <Play className={`w-4 h-4 ${actionLoading === 'resume' ? 'animate-spin' : ''}`} />
                Retomar Fila Global
              </button>
            </div>
          </div>
        )}

        {/* Active Sends */}
        <Section title="Envios em Andamento" count={enviosAtivos.length}>
          {enviosAtivos.length === 0 ? (
            <EmptyState text="Nenhum envio em andamento" />
          ) : (
            <div className="space-y-3">
              {enviosAtivos.map(e => (
                <div key={e.id} className="bg-surface-1/80 border border-white/[0.06] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                      <span className="text-sm font-medium">Log #{e.id}</span>
                      <span className="text-xs text-ink-4">{e.tipo}</span>
                    </div>
                    <span className="text-sm font-mono text-emerald-400">{e.percent}%</span>
                  </div>
                  <div className="w-full bg-surface-2 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${e.percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-ink-4 mt-2">
                    <span>✅ {e.ok || 0} enviados · ❌ {e.erros || 0} erros</span>
                    <span>{(e.ok || 0) + (e.erros || 0)} / {e.total}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Failed Jobs */}
        <Section
          title="Jobs com Falha"
          count={failedJobs.total}
          action={
            failedJobs.total > 0 && (
              <button
                onClick={() => handleAction(queueService.retryAll, 'retryAll')}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg text-xs transition border border-red-600/30"
                disabled={!!actionLoading}
              >
                <RotateCcw className={`w-3 h-3 ${actionLoading === 'retryAll' ? 'animate-spin' : ''}`} />
                Reprocessar Todos
              </button>
            )
          }
        >
          {failedJobs.jobs.length === 0 ? (
            <EmptyState text="Nenhum job com falha 🎉" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-ink-4 border-b border-white/[0.06]">
                    <th className="text-left py-2 px-3">Job</th>
                    <th className="text-left py-2 px-3">Grupo</th>
                    <th className="text-left py-2 px-3">Erro</th>
                    <th className="text-left py-2 px-3">Tentativas</th>
                    <th className="text-right py-2 px-3">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {failedJobs.jobs.map(job => (
                    <tr key={job.id} className="border-b border-white/[0.06] hover:bg-surface-1/50">
                      <td className="py-2 px-3 font-mono text-xs text-ink-3">{job.id}</td>
                      <td className="py-2 px-3">{job.data?.grupo?.nome_grupo || '—'}</td>
                      <td className="py-2 px-3 text-red-400 text-xs max-w-[200px] truncate" title={job.failedReason}>
                        {job.failedReason || '—'}
                      </td>
                      <td className="py-2 px-3 text-center">{job.attemptsMade || 0}</td>
                      <td className="py-2 px-3 text-right">
                        <button
                          onClick={() => handleAction(() => queueService.retryJob(job.id), `retry-${job.id}`)}
                          className="text-emerald-400 hover:text-emerald-300 transition"
                          title="Reprocessar"
                          disabled={!!actionLoading}
                        >
                          <RotateCcw className={`w-4 h-4 ${actionLoading === `retry-${job.id}` ? 'animate-spin' : ''}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-white/[0.06]`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-ink-3">{label}</span>
      </div>
      <span className={`text-2xl font-bold ${color}`}>{value}</span>
    </div>
  );
}

function Section({ title, count, children, action }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          {count > 0 && (
            <span className="bg-surface-2 text-xs px-2 py-0.5 rounded-full text-ink-3">{count}</span>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-surface-1/50 border border-white/[0.06] rounded-xl p-8 text-center">
      <p className="text-ink-4 text-sm">{text}</p>
    </div>
  );
}
